// The inventory's columns, in table order. `sheetHeader` is the heading used
// in the AdMongrel ad inventory spreadsheet; exports write it back so a file
// can go round trip through Google Sheets. `match` lists the starts of
// headings an import accepts for the column (compared lower-case).
//
// Types:
//   text     plain string
//   list     array of strings, written "a, b, c" in a sheet
//   money    cpm + currency, written "23.5 EUR"
//   reach    number, written "30M"
//   contact  contactName + contactEmail, written "Name (email)"

export const FIELDS = [
  {
    key: "channelName",
    label: "Channel name",
    type: "text",
    sheetHeader: "Channel name",
    match: ["channel name", "channel"],
    required: true,
  },
  {
    key: "partner",
    label: "Partner",
    type: "text",
    sheetHeader: "Partner",
    match: ["partner"],
    required: true,
    filter: true,
  },
  {
    key: "territories",
    label: "Territory",
    type: "list",
    sheetHeader: "Territory",
    match: ["territor", "country", "location", "market"],
    filter: true,
  },
  {
    key: "genres",
    label: "Genre",
    type: "list",
    sheetHeader: "Genre",
    match: ["genre"],
    filter: true,
  },
  {
    key: "formats",
    label: "Format",
    type: "list",
    sheetHeader: "Format (HbbTV/CTV/broadcast)",
    match: ["format"],
    options: ["HbbTV", "CTV", "Broadcast"],
    filter: true,
  },
  {
    key: "adFormats",
    label: "Ad format",
    type: "list",
    sheetHeader: "Ad format (video/display)",
    match: ["ad format"],
    options: ["video", "display"],
    filter: true,
  },
  {
    key: "adTypes",
    label: "Ad type",
    type: "list",
    sheetHeader: "Ad type (preroll, pause, etc.)",
    match: ["ad type"],
    options: ["preroll", "midroll", "postroll", "pause", "overlay", "L-banner", "switch-in"],
    filter: true,
  },
  {
    key: "cpm",
    label: "CPM",
    type: "money",
    sheetHeader: "CPM",
    match: ["cpm"],
    numeric: true,
  },
  {
    key: "reach",
    label: "Reach",
    type: "reach",
    sheetHeader: "Reach",
    match: ["reach"],
    numeric: true,
  },
  {
    key: "platform",
    label: "Platform / owner",
    type: "text",
    sheetHeader: "Platform / network / channel owner",
    match: ["platform", "network", "channel owner", "owner"],
    filter: true,
  },
  {
    key: "revshare",
    label: "Revshare",
    type: "text",
    sheetHeader: "Revshare",
    match: ["revshare", "rev share", "revenue share"],
  },
  {
    // Free text. Shown as "Commercial model"; stored as revshareType, its
    // first name, so channels saved before the rename keep their value. Old
    // "Revshare type" sheets still import into it: those headings are longer
    // than Revshare's, so they never land in Revshare.
    key: "revshareType",
    label: "Commercial model",
    type: "text",
    sheetHeader: "Commercial model",
    match: ["commercial model", "revshare type", "rev share type", "revenue share type"],
  },
  {
    key: "contact",
    label: "Contact",
    type: "contact",
    sheetHeader: "Contact name",
    match: ["contact"],
  },
  {
    // Bookkeeping, set by the panel: shown and exported, never imported or
    // edited, and not counted as a change in the history
    key: "updatedAt",
    label: "Last updated",
    type: "date",
    sheetHeader: "Last updated",
    match: [],
    numeric: true,
    meta: true,
  },
];

export const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f]));

export const FILTER_FIELDS = FIELDS.filter((f) => f.filter);

// A brand-new, empty channel
export function emptyChannel() {
  return {
    channelName: "",
    partner: "",
    territories: [],
    genres: [],
    formats: [],
    adFormats: [],
    adTypes: [],
    cpm: null,
    currency: "EUR",
    reach: null,
    platform: "",
    revshare: "",
    revshareType: "",
    contactName: "",
    contactEmail: "",
  };
}

// What makes a channel the same channel: partner, name, territory and
// format, ignoring case and order. The sheet lists one channel per country
// (and sometimes per format) on separate rows, with their own price, so
// partner and name alone are not enough.
export function channelKey(c) {
  const list = (v) => (v || []).map((x) => String(x).trim().toLowerCase()).sort().join(",");
  const text = (v) => String(v || "").trim().toLowerCase();
  return [text(c.partner), text(c.channelName), list(c.territories), list(c.formats)].join("|");
}

// An imported row without its empty cells, so merging it into a channel keeps
// what the channel already has there. A CPM's currency goes with its amount:
// an empty CPM cell still says "EUR" (the default) and must not change it.
export function filledCells(row) {
  const out = {};
  Object.entries(row).forEach(([k, v]) => {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;
    out[k] = v;
  });
  if (row.cpm == null) delete out.currency;
  return out;
}

// The values a filter compares against, always as a list
export function valuesOf(row, key) {
  const v = row[key];
  if (Array.isArray(v)) return v;
  return v ? [v] : [];
}
